// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * ARCHAT — Feature flags desde backend (cache TTL, kill-switch, rollout).
 * Sin servicios externos. useFeature(name) para checks en UI.
 *
 * Debe existir `<FeatureFlagProvider>` dentro de `<AuthProvider>` (App.jsx): la carga
 * espera a que termine el bootstrap de auth y se vuelve a disparar al autenticarse.
 *
 * @file frontend/src/context/FeatureFlagContext.jsx
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { API_ENDPOINTS, joinApiOrigin } from '../utils/constants';
import { request as apiClientRequest, getToken } from '../services/apiClient';
import { useAuth } from './AuthContext';

/** `reload: null` permite detectar en dev si falta FeatureFlagProvider. */
export const FeatureFlagContext = createContext({
  flags: {},
  loading: true,
  useFeature: () => false,
  reload: null,
});

function ffLog(...args) {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info(...args);
  }
}

export function FeatureFlagProvider({ children }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(false);

  const load = useCallback(async () => {
    const url = joinApiOrigin(API_ENDPOINTS.FEATURE_FLAGS || '/api/feature_flags.php');
    let res = null;
    setLoading(true);
    try {
      ffLog('[FEATURE_FLAGS]', 'fetch start', { url, hasToken: !!getToken() });
      res = await apiClientRequest(url, { credentials: 'include' });
      if (!res.ok) {
        ffLog('[FEATURE_FLAGS]', 'fetch no OK', { url, status: res.status, statusText: res.statusText });
        setFlags({});
        return;
      }
      const data = await res.json();
      setFlags(typeof data === 'object' && data !== null ? data : {});
      const keys = typeof data === 'object' && data !== null ? Object.keys(data) : [];
      ffLog('[FEATURE_FLAGS]', 'fetch OK', {
        url,
        keyCount: keys.length,
        FEATURE_CONVERSATION_SUBJECTS_V1: data?.FEATURE_CONVERSATION_SUBJECTS_V1,
      });
    } catch (err) {
      ffLog('[FEATURE_FLAGS]', 'fetch error', { url, message: err?.message ?? String(err) });
      setFlags({});
    } finally {
      setLoading(false);
      ffLog('[FEATURE_FLAGS]', 'loading=false', { lastStatus: res?.status ?? null });
    }
  }, []);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      ffLog('[FEATURE_FLAGS]', 'provider mounted');
    }
  }, []);

  const authUserId = user?.id ?? user?.usuario_id ?? null;

  useEffect(() => {
    if (isLoading) {
      ffLog('[FEATURE_FLAGS]', 'auth changed', { phase: 'auth_loading', isLoading: true });
      setFlags({});
      setLoading(false);
      return;
    }

    ffLog('[FEATURE_FLAGS]', 'auth changed', {
      phase: 'auth_ready',
      isAuthenticated,
      hasToken: !!getToken(),
      userId: authUserId,
    });

    if (!isAuthenticated) {
      setFlags({});
      setLoading(false);
      return;
    }

    void load();
  }, [isLoading, isAuthenticated, authUserId, load]);

  const useFeature = useCallback(
    (name) => {
      return !!flags[name];
    },
    [flags]
  );

  const value = { flags, loading, useFeature, reload: load };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlag(name) {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) return false;
  if (typeof ctx.reload !== 'function') {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error(
        '[FEATURE_FLAGS] FeatureFlagProvider no está montado en App.jsx; useFeatureFlag("',
        name,
        '") devuelve false y no hay request a feature_flags.php.'
      );
    }
    return false;
  }
  return ctx.useFeature(name);
}

/** Hook useFeature(name) — alias. */
export function useFeature(name) {
  return useFeatureFlag(name);
}
